import React from "react";
import { Card, Button, Alert, message } from "antd";
import {
    CopyOutlined,
    InfoCircleOutlined,
    SyncOutlined,
    SwapOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { MolliePaymentStatus } from "../../generated/graphql";
import { formatIban } from "./formatIban";
import css from "./BankTransferDetails.module.css";

/**
 * Phase 24 / Plan 24-06 / D-10:
 * BankTransferDetailsSection ist die Card-Internals OHNE äusseren Ant-<Card>-Wrapper —
 * wird in MolliePaymentReturnCard eingebettet (Sketch-Variant-D-Integration). Der
 * Backwards-Compat-Export BankTransferDetails (am Ende der Datei) wickelt die Section
 * weiterhin in eine Card ein, damit existing Imports und Tests unverändert weiterlaufen.
 */

export interface BankTransferDetailsProps {
    status: MolliePaymentStatus;
}

interface FieldRow {
    labelKey: string;
    value: string;
    monoFont: boolean;
    displaySize: boolean;
}

async function copyToClipboard(value: string, t: (k: string) => string): Promise<void> {
    try {
        await navigator.clipboard.writeText(value);
        message.success(t("mollieCheckout.bankDetails.copied"), 2);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[BankTransferDetails] clipboard write failed", e);
        message.error(t("mollieCheckout.bankDetails.copyFailed"), 2);
    }
}

function formatAmount(value: string, currency: string): string {
    // "100.00" + "EUR" -> "100,00 EUR" (de-Format)
    const germanValue = value.replace(".", ",");
    return `${germanValue} ${currency}`;
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;

/**
 * Letzter Tag, an dem Mollie die Überweisung noch zuordnet. Mollie setzt
 * `expiresAt` auf den Tag NACH dem letzten gültigen Tag (04:00 UTC), an diesem
 * Morgen ist die Referenz bereits abgelaufen. Gleiche Regel wie im Backend
 * (ko-X-main `resolveMollieBanktransferLastValidDay`, #1036), damit Rechnung,
 * E-Mail und diese Seite dasselbe Datum zeigen.
 *
 * Feste Zeitzone statt Browser-Zeitzone: 04:00 UTC liegt in Mitteleuropa immer
 * am selben Kalendertag, 24 Stunden zurück treffen deshalb sicher den Vortag.
 */
function formatLastValidDay(expiresAt: string | Date | null | undefined): string | null {
    if (!expiresAt) {
        return null;
    }
    const date = new Date(expiresAt);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return new Date(date.getTime() - DAY_IN_MS).toLocaleDateString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone: "Europe/Berlin",
    });
}

/**
 * BankTransferDetailsSection — Card-Internals OHNE äusseren Ant-<Card>-Wrapper.
 * Plan 24-06 / D-10: Wird in MolliePaymentReturnCard eingebettet, damit Banktransfer-
 * Daten Teil der 720px-EUA-Card werden (statt eigene Card-Wrapping). Returns null
 * when status.bankTransferDetails is missing — identisch zum bisherigen Verhalten.
 */
export function BankTransferDetailsSection({ status }: BankTransferDetailsProps): JSX.Element | null {
    const { t } = useTranslation();
    const details = status.bankTransferDetails;
    const amountInfo = status.amountDetails;

    if (!details) {
        return null;
    }

    const rows: FieldRow[] = [
        {
            labelKey: "mollieCheckout.bankDetails.iban",
            value: formatIban(details.iban),
            monoFont: true,
            displaySize: false,
        },
    ];
    if (details.bic) {
        rows.push({
            labelKey: "mollieCheckout.bankDetails.bic",
            value: details.bic,
            monoFont: true,
            displaySize: false,
        });
    }
    rows.push({
        labelKey: "mollieCheckout.bankDetails.recipient",
        value: details.recipient,
        monoFont: false,
        displaySize: false,
    });
    rows.push({
        labelKey: "mollieCheckout.bankDetails.reference",
        value: details.reference,
        monoFont: true,
        displaySize: false,
    });
    if (amountInfo) {
        rows.push({
            labelKey: "mollieCheckout.bankDetails.amount",
            value: formatAmount(amountInfo.value, amountInfo.currency),
            monoFont: true,
            displaySize: true,
        });
    }
    const expiresAt = formatLastValidDay(status.expiresAt);
    if (expiresAt) {
        rows.push({
            labelKey: "mollieCheckout.bankDetails.dueDate",
            value: expiresAt,
            monoFont: false,
            displaySize: false,
        });
    }

    return (
        <section className={css.section}>
            <div className={css.sectionHeader}>
                <span className={css.title}>
                    <InfoCircleOutlined className={css.titleIcon} />{" "}
                    {t("mollieCheckout.bankDetails.title")}
                </span>
            </div>
            <p className={css.subtitle}>{t("mollieCheckout.bankDetails.subtitle")}</p>
            <p className={css.recipientHint}>{t("mollieCheckout.bankDetails.recipientHint")}</p>
            <ul className={css.fieldList}>
                {rows.map((row, idx) => {
                    const label = t(row.labelKey);
                    const copyLabel = t("mollieCheckout.bankDetails.copyAriaLabel", { fieldLabel: label });
                    return (
                        <li key={idx} className={css.fieldRow}>
                            <span className={css.fieldLabel}>{label}</span>
                            <span
                                className={[
                                    css.fieldValue,
                                    row.monoFont ? css.mono : "",
                                    row.displaySize ? css.displaySize : "",
                                ].filter(Boolean).join(" ")}
                            >
                                {row.value}
                            </span>
                            <Button
                                type="text"
                                icon={<CopyOutlined />}
                                aria-label={copyLabel}
                                onClick={() => void copyToClipboard(row.value, t)}
                                className={css.copyButton}
                            />
                        </li>
                    );
                })}
            </ul>
            {(status.statusUrl || status.payOnlineUrl) && (
                <div className={css.exitLinks}>
                    {status.statusUrl && (
                        <a
                            className={css.exitLink}
                            href={status.statusUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <SyncOutlined className={css.exitLinkIcon} />
                            {t("mollieCheckout.bankDetails.checkStatus")}
                        </a>
                    )}
                    {status.payOnlineUrl && (
                        <a
                            className={css.exitLink}
                            href={status.payOnlineUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <SwapOutlined className={css.exitLinkIcon} />
                            {t("mollieCheckout.bankDetails.chooseOtherMethod")}
                        </a>
                    )}
                </div>
            )}
            <Alert
                type="info"
                showIcon
                message={t("mollieCheckout.bankDetails.emailHint")}
                className={css.alert}
            />
        </section>
    );
}

/**
 * BankTransferDetails — Backwards-Compat-Wrapper, der die Section in eine Ant-<Card>
 * einwickelt. Existing-Imports (z.B. PaymentReturn.tsx vor Plan 24-06 Task 2b) und
 * existing Tests (BankTransferDetails.test.tsx) bleiben funktional unverändert.
 */
export function BankTransferDetails({ status }: BankTransferDetailsProps): JSX.Element | null {
    if (!status.bankTransferDetails) {
        return null;
    }
    return (
        <Card type="inner" className={css.card} bordered>
            <BankTransferDetailsSection status={status} />
        </Card>
    );
}
